import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Clock, Users, Radio, BellRing, X, Bell, Sun, ShieldCheck, Pin, PinOff } from "lucide-react";
import BypassTicketIcon from "../components/BypassTicketIcon";
import { toast } from "sonner";
import { useTicketNotification } from "../hooks/useTicketNotification";
import { usePersistentTicketNotification } from "../hooks/usePersistentTicketNotification";
import { useWakeLock } from "../hooks/useWakeLock";
import { useTicketThresholdAlerts } from "../hooks/useTicketThresholdAlerts";
import { useTicketCode } from "../hooks/useTicketCode";
import { formatTicketCode } from "../lib/ticketCode";
import ConfirmDialog from "../components/ConfirmDialog";
import NotifPermissionPrompt from "../components/NotifPermissionPrompt";
import { usePinnedTickets } from "../hooks/usePinnedTickets";
import { pinTicket, unpinTicket } from "../lib/pinnedTickets";

export default function MyTicket() {
  const [tickets, setTickets] = useState([]);
  const [focusedId, setFocusedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCalledOverlay, setShowCalledOverlay] = useState(false);
  const [persistentOn, setPersistentOn] = useState(true);
  const [wakeLockOn, setWakeLockOn] = useState(false);
  const [flashKey, setFlashKey] = useState(null); // t3 | t2 | t1 | t0 | called
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const lastStatusRef = useRef(null);
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get("focus");
  const pinnedMap = usePinnedTickets();

  // Resilient polling: on transient errors (5xx / network), keep previous state.
  const load = async () => {
    try {
      const { data } = await api.get("/tickets/my-active-list");
      const list = Array.isArray(data) ? data : [];
      setTickets(list);
      // Keep focus on the same ticket id if it's still active, else fall
      // back to the most-imminent ticket. NOTE: the `?focus=<id>` URL
      // parameter (set when the visitor taps a header pill) is handled in
      // its OWN effect below so it can override a stale `prev` focus and
      // switch the detail card every time, including when the visitor
      // re-clicks a pill while already on /my-ticket.
      setFocusedId((prev) => {
        if (prev && list.some((t) => t.id === prev)) return prev;
        return list[0]?.id || null;
      });
      return list;
    } catch (err) {
      if (err.response?.status === 401) {
        nav("/login");
        return null;
      }
      // transient error: do NOT wipe ticket state — keep last known
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  // Once the focus param is honored, strip it from the URL so navigating
  // away & back doesn't re-focus an obsolete ticket id. We ALSO use this
  // effect as the authoritative switcher: every time `focusParam` changes
  // (visitor taps another header pill), force the detail card to display
  // that ticket — overriding any previous focus, even if we are already on
  // /my-ticket. The effect runs on every focusParam change, so a re-click
  // on the same pill (focusParam temporarily set then cleared then set
  // again) still works because React-Router emits a fresh value.
  useEffect(() => {
    if (focusParam) {
      setFocusedId(focusParam);
      const sp = new URLSearchParams(searchParams);
      sp.delete("focus");
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusParam]);

  const ticket = tickets.find((t) => t.id === focusedId) || tickets[0] || null;
  // The MOST imminent ticket drives the threshold/persistent alerts so the
  // visitor is always notified about the ticket closest to being called,
  // regardless of which one they're currently viewing in detail.
  const primaryTicket = tickets[0] || null;

  // Hook: audio chime + vibration + browser notification on waiting→called
  // Drive these from the MOST IMMINENT ticket so the visitor is alerted about
  // the ticket about to be called (regardless of which one is being viewed).
  useTicketNotification(primaryTicket);
  // Persistent updating system notification (bulle-like effect in notif tray)
  usePersistentTicketNotification(primaryTicket, persistentOn);
  // Threshold alerts — distinct chime + vibration + notif at positions 3 / 2 / 1 / 0 / called
  useTicketThresholdAlerts(primaryTicket, (k) => {
    setFlashKey(k);
    setTimeout(() => setFlashKey(null), 1600);
  });
  // Optional: keep screen on
  const wake = useWakeLock(wakeLockOn && !!primaryTicket);

  // Rotating 4-digit verification code (changes every 30s) — visitor side
  // (bound to the ticket currently being viewed in detail)
  const codeState = useTicketCode(ticket?.id, "visitor", !!ticket);

  // Live clock updated every second (displayed on the ticket card)
  const [nowStr, setNowStr] = useState(() => new Date().toLocaleTimeString("fr-FR"));
  useEffect(() => {
    const t = setInterval(() => setNowStr(new Date().toLocaleTimeString("fr-FR")), 1000);
    return () => clearInterval(t);
  }, []);

  // Fullscreen overlay trigger when status flips to called
  // Only triggers for the FOCUSED ticket; if another ticket is called while
  // not focused, the user can switch tab to see it. (Notification still fires
  // for the primary ticket via useTicketNotification.)
  useEffect(() => {
    const prev = lastStatusRef.current;
    if (ticket) {
      if (prev === "waiting" && ticket.status === "called") {
        setShowCalledOverlay(true);
      }
      lastStatusRef.current = ticket.status;
    } else {
      lastStatusRef.current = null;
    }
  }, [ticket?.status]); // eslint-disable-line

  const cancel = () => {
    if (!ticket) return;
    setCancelConfirm(true);
  };

  const performCancel = async () => {
    setCancelConfirm(false);
    if (!ticket) return;
    try {
      await api.post(`/tickets/${ticket.id}/cancel`);
      toast.success("Ticket annulé");
      setShowCalledOverlay(false);
      // Refresh full list and refocus to next-imminent ticket if any.
      const remaining = tickets.filter((t) => t.id !== ticket.id);
      setTickets(remaining);
      setFocusedId(remaining[0]?.id || null);
      // Background refresh to get authoritative state
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const requestNotifPerm = async () => {
    if (!("Notification" in window)) {
      toast.error("Notifications non supportées par ce navigateur");
      return;
    }
    try {
      const p = await Notification.requestPermission();
      setNotifPermission(p);
      if (p === "granted") {
        toast.success("Notifications activées · suivi en temps réel");
      } else if (p === "denied") {
        toast.error("Notifications refusées — vous pouvez les réactiver dans les réglages du navigateur");
      }
    } catch {
      toast.error("Impossible d'activer les notifications");
    }
  };

  if (loading) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  if (!ticket) {
    return (
      <div className="container mx-auto px-4 py-20 max-w-md text-center" data-testid="ticket-empty-state">
        <div className="mx-auto inline-flex items-center justify-center w-24 h-24 mb-4">
          <BypassTicketIcon size={88} />
        </div>
        <h1 className="font-display text-3xl font-bold text-ink">Aucun ticket actif</h1>
        <p className="mt-3 text-subtle">Prenez un ticket à distance auprès d'un établissement partenaire.</p>
        <Button asChild className="mt-6 bg-brand hover:bg-brand-hover rounded-md h-12 px-8 text-white" data-testid="browse-link">
          <Link to="/search">Parcourir les établissements</Link>
        </Button>
        <div className="mt-8 text-sm">
          <Link to="/my-tickets" className="text-brand font-medium" data-testid="link-history">Voir mon historique →</Link>
        </div>
      </div>
    );
  }

  const isRemote = ticket.type === "priority";
  const isCalled = ticket.status === "called";
  // Spec utilisateur fork 24 — ticket annulé suite à la suppression du
  // compte établissement. Le visiteur voit un bandeau rouge dédié à la
  // place de son ticket actif.
  const isCommerceDeleted = ticket.cancelReason === "commerce_deleted";
  // Spec utilisateur fork 26 — ticket annulé parce que l'établissement
  // a fermé sa file manuellement. Le visiteur voit un bandeau rouge
  // l'invitant à contacter l'établissement (téléphone surfacé si dispo).
  const isQueueClosed = ticket.cancelReason === "queue_closed";

  const dismissCommerceDeleted = async () => {
    try {
      await api.post(`/tickets/${ticket.id}/dismiss-commerce-deleted`);
      // Recharger immédiatement la liste.
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la fermeture du message.");
    }
  };

  const dismissQueueClosed = async () => {
    try {
      await api.post(`/tickets/${ticket.id}/dismiss-queue-closed`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la fermeture du message.");
    }
  };

  if (isQueueClosed) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-8 md:py-14 max-w-2xl" data-testid="ticket-queue-closed-banner">
        <div className="border-2 border-destructive bg-destructive/5 rounded-xl p-6 md:p-8">
          <div className="flex items-start gap-3 mb-4">
            <span aria-hidden="true" className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-destructive text-white text-xl shrink-0">●</span>
            <div className="min-w-0">
              <p className="overline text-destructive text-[10px] mb-1">Ticket annulé</p>
              <h2 className="font-display text-2xl md:text-3xl font-black text-destructive leading-tight">
                Établissement actuellement fermé
              </h2>
            </div>
          </div>
          <p className="text-sm md:text-base text-ink leading-relaxed">
            L'établissement <strong>« {ticket.commerceName || "—"} »</strong> a fermé sa file d'attente. Votre ticket a été automatiquement annulé.
          </p>
          <p className="text-sm md:text-base text-ink leading-relaxed mt-3">
            Pour en savoir plus, nous vous invitons à contacter directement l'établissement.
          </p>
          {ticket.commercePhone ? (
            <a
              href={`tel:${ticket.commercePhone}`}
              className="mt-4 inline-flex items-center gap-2 h-11 px-4 bg-destructive text-white font-bold text-sm uppercase tracking-wider rounded-md"
              data-testid="ticket-queue-closed-call-btn"
            >
              Appeler · {ticket.commercePhone}
            </a>
          ) : null}
          <div className="mt-6 flex justify-end">
            <Button
              onClick={dismissQueueClosed}
              className="bg-destructive hover:bg-destructive/90 text-white rounded-md h-11 px-6 font-display font-bold uppercase tracking-wider text-xs"
              data-testid="dismiss-queue-closed-btn"
            >
              J'ai compris
            </Button>
          </div>
        </div>
        <div className="mt-6 text-center">
          <Link to="/search" className="text-brand font-medium text-sm">Parcourir d'autres établissements →</Link>
        </div>
      </div>
    );
  }

  if (isCommerceDeleted) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-8 md:py-14 max-w-2xl" data-testid="ticket-commerce-deleted-banner">
        <div className="border-2 border-destructive bg-destructive/5 rounded-xl p-6 md:p-8">
          <div className="flex items-start gap-3 mb-4">
            <span aria-hidden="true" className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-destructive text-white text-xl shrink-0">●</span>
            <div className="min-w-0">
              <p className="overline text-destructive text-[10px] mb-1">Ticket annulé</p>
              <h2 className="font-display text-2xl md:text-3xl font-black text-destructive leading-tight">
                Ticket annulé
              </h2>
            </div>
          </div>
          <p className="text-sm md:text-base text-ink leading-relaxed">
            L'établissement <strong>« {ticket.commerceName || "—"} »</strong> a supprimé son compte BypassGo et mis fin à cette file d'attente.
          </p>
          <p className="text-sm md:text-base text-ink leading-relaxed mt-3">
            Votre ticket a été automatiquement annulé.
          </p>
          <p className="text-sm md:text-base text-ink leading-relaxed mt-3">
            Aucun appel ne sera effectué sur cette file d'attente et ce ticket ne peut plus être utilisé.
          </p>
          <p className="text-sm md:text-base text-ink leading-relaxed mt-3">
            Pour toute information complémentaire, nous vous invitons à contacter directement l'établissement concerné.
          </p>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={dismissCommerceDeleted}
              className="bg-destructive hover:bg-destructive/90 text-white rounded-md h-11 px-6 font-display font-bold uppercase tracking-wider text-xs"
              data-testid="dismiss-commerce-deleted-btn"
            >
              J'ai compris
            </Button>
          </div>
        </div>
        <div className="mt-6 text-center">
          <Link to="/search" className="text-brand font-medium text-sm">Parcourir d'autres établissements →</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes bpg-pulse-ring {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes bpg-bounce-soft {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes bpg-flash {
          0%,100% { background: #FFFBE6; }
          50% { background: #FFF4A1; }
        }
        @keyframes bpg-threshold-pulse {
          0%   { transform: scale(1);   box-shadow: 0 0 0 0 rgba(0,47,167,0.55); }
          50%  { transform: scale(1.02); box-shadow: 0 0 0 16px rgba(0,47,167,0); }
          100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(0,47,167,0); }
        }
        @keyframes bpg-threshold-flash-bg {
          0%   { background: #E0E8FF; }
          100% { background: #fff; }
        }
        .bpg-called-card { animation: bpg-flash 1.4s ease-in-out infinite; }
        .bpg-called-number { animation: bpg-bounce-soft 1s ease-in-out infinite; }
        .bpg-threshold-flash { animation: bpg-threshold-pulse 1.4s ease-out 1, bpg-threshold-flash-bg 1.4s ease-out 1; }
        .bpg-pulse-dot::after {
          content:""; position:absolute; inset:0; border-radius:9999px;
          background: currentColor; opacity: 0.5;
          animation: bpg-pulse-ring 1.3s ease-out infinite;
        }
      `}</style>

      {/* FULLSCREEN OVERLAY when called */}
      {showCalledOverlay && isCalled && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/90 backdrop-blur-sm"
          data-testid="called-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative bg-[#FFE75E] border-[6px] border-ink w-full max-w-lg p-8 md:p-12 text-center shadow-2xl">
            <button
              onClick={() => setShowCalledOverlay(false)}
              className="absolute top-3 right-3 p-2 hover:bg-ink/10"
              data-testid="close-overlay-btn"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>
            <div className="relative inline-flex items-center justify-center w-20 h-20 bg-ink text-[#FFE75E] rounded-full mb-6">
              <BellRing size={40} />
              <span className="absolute inset-0 rounded-full bpg-pulse-dot text-ink" />
            </div>
            <p className="overline mb-2 text-ink">{ticket.commerceName}</p>
            <h2 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-ink">C'est à vous !</h2>
            <p className="mt-4 font-mono text-7xl md:text-8xl font-black leading-none text-ink bpg-called-number">
              {formatTicketCode(ticket)}
            </p>
            <p className="mt-6 text-ink font-semibold text-lg">Présentez-vous au comptoir maintenant.</p>
            <Button
              onClick={() => setShowCalledOverlay(false)}
              className="mt-8 bg-ink hover:bg-ink text-[#FFE75E] rounded-none h-12 px-8 font-bold tracking-wider uppercase text-xs"
              data-testid="acknowledge-overlay-btn"
            >
              J'y vais
            </Button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 md:px-8 py-3 md:py-10 max-w-2xl">
        {/* Multi-ticket switcher — visible only when the visitor has 2 or 3
            active tickets. The selected tab drives the detailed card below. */}
        {tickets.length > 1 && (
          <div className="mb-3 md:mb-6" data-testid="active-tickets-switcher">
            <p className="overline mb-2 flex items-center justify-between">
              <span>Mes {tickets.length} tickets en cours</span>
              <span className="text-[10px] text-subtle font-normal normal-case tracking-normal">
                {tickets.length} / 3
              </span>
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {tickets.map((t) => {
                const selected = t.id === focusedId;
                const tCalled = t.status === "called";
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setFocusedId(t.id)}
                    className={`relative text-left px-3 py-2 border-2 transition-all ${
                      selected
                        ? "border-ink bg-ink text-white"
                        : tCalled
                        ? "border-brand bg-brand/5 text-ink"
                        : "border-border bg-white text-ink hover:border-ink/50"
                    }`}
                    data-testid={`active-ticket-tab-${t.id}`}
                  >
                    <p className={`overline mb-0.5 ${selected ? "text-white/70" : ""}`}>
                      {t.commerceName || "—"}
                    </p>
                    <p className="font-mono font-black text-lg leading-none">
                      {formatTicketCode(t)}
                    </p>
                    <p className={`mt-1 text-[10px] ${selected ? "text-white/70" : "text-subtle"} uppercase tracking-wider font-bold`}>
                      {tCalled
                        ? "Appelé"
                        : t.position === 0
                        ? "Prochain"
                        : `${t.position} devant`}
                    </p>
                    {tCalled && !selected && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
                        <span className="relative inline-flex h-3 w-3 rounded-full bg-brand" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          className={`border-2 border-ink ${isCalled ? "bpg-called-card" : "bg-white"} ${flashKey && !isCalled ? "bpg-threshold-flash" : ""}`}
          data-testid="my-ticket-card"
        >
          {/* Spec utilisateur Feb 2026 — bandeau orange affiché DÈS que
             l'établissement suspend la file en cours (principale, ponctuelle
             OU supplémentaire). Le ticket reste actif (la file n'est pas
             fermée), mais le visiteur sait que la prise en charge est
             temporairement suspendue. */}
          {ticket.queueIsSuspended && (
            <div
              className="p-3 md:p-4 border-b-2 border-[#F18F18] bg-[#FFF4DD] flex items-start gap-3"
              role="status"
              aria-live="polite"
              data-testid="ticket-suspended-banner"
            >
              <span aria-hidden="true" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F18F18] text-white text-sm font-black shrink-0">!</span>
              <p className="font-bold text-[#A65800] text-sm md:text-base leading-snug">
                La file est suspendue par l'établissement
              </p>
            </div>
          )}
          <div className="p-3 md:p-6 border-b border-border flex items-center justify-between">
            <div className="min-w-0">
              <p className="overline mb-0.5">{ticket.commerceName}</p>
              <p className="text-sm text-subtle">
                Votre ticket
                {ticket.queueName ? (
                  <>
                    <span className="mx-1.5 text-border">·</span>
                    <span className="font-bold text-ink uppercase tracking-wide" data-testid="ticket-queue-name">
                      File {ticket.queueName}
                    </span>
                  </>
                ) : null}
              </p>
            </div>
            {isRemote && (
              <span className="inline-flex items-center gap-1.5 bg-brand text-white px-2.5 py-1 font-bold uppercase tracking-wider text-[10px] md:text-xs shrink-0">
                <Radio size={12} /> À distance
              </span>
            )}
          </div>
          <div className="px-4 py-3 md:p-10 text-center">
            <p className="overline mb-2 md:mb-4">{isCalled ? "C'est à vous !" : "Votre numéro"}</p>
            <p
              className={`font-mono text-6xl md:text-9xl font-bold tracking-tighter text-ink leading-none ${isCalled ? "bpg-called-number" : ""}`}
              data-testid="ticket-number"
            >
              {formatTicketCode(ticket)}
            </p>
            {/* Currently-being-called ticket on the establishment side, so the
                visitor instantly knows how close they are. ALWAYS shown when
                we know the queue letter — falls back to "<letter>000" when no
                ticket has been called yet on the current session. */}
            {ticket.queueCurrentCode ? (
              <div
                className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-surface border border-border"
                data-testid="ticket-current-call"
                title="Ticket actuellement appelé par l'établissement"
              >
                <Radio size={12} className="text-brand shrink-0" />
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-subtle">Numéro en cours</span>
                <span
                  className="font-mono text-lg font-black text-ink leading-none ml-1"
                  data-testid="ticket-current-call-value"
                >
                  {ticket.queueCurrentCode}
                </span>
              </div>
            ) : null}
            <p className={`mt-3 md:mt-6 text-base md:text-lg font-semibold ${isCalled ? "text-brand" : "text-ink"}`}>
              {isCalled
                ? "Présentez-vous au comptoir"
                : ticket.position === 0
                ? "Vous êtes le prochain"
                : `${ticket.position} visiteur${ticket.position > 1 ? "s" : ""} avant vous`}
            </p>
            {flashKey && !isCalled && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-brand text-white text-xs font-bold uppercase tracking-wider animate-pulse" data-testid="threshold-badge">
                <Bell size={12} />
                {flashKey === "t0" && "Vous êtes le prochain"}
                {flashKey === "t1" && "Plus qu'1 personne"}
                {flashKey === "t2" && "Plus que 2 personnes"}
                {flashKey === "t3" && "Plus que 3 personnes"}
              </div>
            )}
            {isCalled && (
              <div className="mt-4 inline-flex items-center gap-2 text-brand font-bold uppercase tracking-wider text-xs">
                <span className="relative inline-flex w-2 h-2 rounded-full bg-brand bpg-pulse-dot" /> En cours d'appel
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 border-t border-border">
            <div className="p-3 md:p-5 border-r border-border">
              <p className="overline flex items-center gap-1"><Users size={12} /> Position</p>
              <p className="mt-1 md:mt-2 font-mono text-xl md:text-2xl font-bold text-ink">{ticket.position}</p>
            </div>
            <div className="p-3 md:p-5">
              <p className="overline flex items-center gap-1"><Clock size={12} /> Estimé</p>
              <p className="mt-1 md:mt-2 font-mono text-xl md:text-2xl font-bold text-ink">
                {ticket.estimatedWait}
                <span className="text-sm font-normal text-subtle"> min</span>
              </p>
            </div>
          </div>

          {/* VERIFICATION CODE — rotating 4-digit TOTP-like */}
          <div className="border-t border-border p-3 md:p-5" data-testid="verification-code-block">
            <div className="flex items-center justify-between">
              <p className="overline">Code de vérification</p>
              <span className="text-xs font-mono text-subtle" data-testid="ticket-clock">{nowStr}</span>
            </div>
            <div className="mt-2 md:mt-3 flex items-end justify-between gap-4">
              <p
                className="font-mono text-4xl md:text-6xl font-black tracking-[0.25em] text-ink leading-none"
                data-testid="verification-code"
              >
                {codeState.loading ? "----" : codeState.code || "----"}
              </p>
              <div className="flex-1 max-w-[120px]">
                <div className="h-2 bg-surface overflow-hidden border border-border">
                  <div
                    className="h-full bg-brand transition-all duration-1000 linear"
                    style={{
                      width: codeState.remainingSec != null
                        ? `${(codeState.remainingSec / codeState.total) * 100}%`
                        : "0%",
                    }}
                  />
                </div>
                <p className="text-[10px] text-subtle text-right mt-1 font-mono" data-testid="code-remaining">
                  {codeState.remainingSec != null ? `${codeState.remainingSec}s` : "—"}
                </p>
              </div>
            </div>
            <p className="mt-2 text-[10px] md:text-xs text-subtle leading-snug">
              Montrez ce code à l'établissement à votre arrivée. Il change toutes les {codeState.total} secondes — une capture d'écran n'est plus valable.
            </p>
          </div>
        </div>
        <div className="mt-3 md:mt-6 grid grid-cols-2 gap-2 md:gap-3">
          <Button
            onClick={() => {
              if (!ticket) return;
              if (pinnedMap[ticket.id]) {
                unpinTicket(ticket.id);
                toast.success("Bulle retirée");
              } else {
                pinTicket(ticket.id, ticket);
                toast.success("Bulle épinglée — visible sur tous les écrans BypassGo", {
                  description: "Déplacez-la au doigt, réduisez-la ou fermez-la. Pour la voir hors de l'app, installez BypassGo via 'Ajouter à l'écran d'accueil'.",
                  duration: 5500,
                });
              }
            }}
            variant="outline"
            className={`h-11 md:h-12 rounded-md border ${
              ticket && pinnedMap[ticket.id]
                ? "border-brand text-brand bg-brand/5 hover:bg-brand/10"
                : "border-border text-ink hover:border-brand hover:text-brand"
            }`}
            data-testid="pin-ticket-btn"
            aria-label={ticket && pinnedMap[ticket.id] ? "Retirer la bulle épinglée" : "Épingler la file"}
          >
            {ticket && pinnedMap[ticket.id] ? (
              <><PinOff size={16} className="mr-2" /> Retirer la bulle</>
            ) : (
              <><Pin size={16} className="mr-2" /> Épingler la file</>
            )}
          </Button>
          <Button
            onClick={cancel}
            variant="outline"
            className="h-11 md:h-12 border border-border text-subtle hover:text-destructive hover:border-destructive rounded-md"
            data-testid="cancel-ticket-btn"
          >
            Annuler mon ticket
          </Button>
        </div>

        {/* LIVE TRACKING PANEL */}
        <div className="mt-6 border border-border" data-testid="live-tracking-panel">
          <div className="p-3 border-b border-border bg-surface/60 flex items-center gap-2">
            <Bell size={14} className="text-brand" />
            <span className="overline">Suivi en temps réel</span>
          </div>
          <div className="p-4 space-y-3">
            {notifPermission !== "granted" ? (
              <div className="flex items-start gap-3">
                <BellRing size={18} className="text-brand mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">Activez les notifications</p>
                  <p className="text-xs text-subtle mt-0.5">
                    Votre position apparaîtra dans la barre de notifications de votre téléphone, mise à jour automatiquement. Plus besoin de garder l'app ouverte.
                  </p>
                  <Button
                    onClick={requestNotifPerm}
                    className="mt-2 h-9 px-4 bg-brand hover:bg-brand-hover text-white rounded-md text-sm"
                    data-testid="enable-notif-btn"
                  >
                    Activer
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-[#0A7F2E] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">Notifications actives</p>
                  <p className="text-xs text-subtle mt-0.5">
                    Position mise à jour dans la barre de notifications. Un son + vibration retentiront quand ce sera votre tour.
                  </p>
                </div>
                <button
                  onClick={() => setPersistentOn((v) => !v)}
                  className={`text-xs font-bold uppercase tracking-wider px-2 py-1 border ${persistentOn ? "border-brand text-brand" : "border-border text-subtle"}`}
                  data-testid="toggle-persistent-notif"
                >
                  {persistentOn ? "Actif" : "Inactif"}
                </button>
              </div>
            )}

            {wake.supported && (
              <div className="flex items-start gap-3 pt-3 border-t border-border">
                <Sun size={18} className={`${wakeLockOn && wake.active ? "text-[#E89B2F]" : "text-subtle"} mt-0.5 shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">Garder l'écran allumé</p>
                  <p className="text-xs text-subtle mt-0.5">
                    Empêche le téléphone de se mettre en veille pendant que vous attendez.
                  </p>
                </div>
                <button
                  onClick={() => setWakeLockOn((v) => !v)}
                  className={`text-xs font-bold uppercase tracking-wider px-2 py-1 border ${wakeLockOn ? "border-brand text-brand" : "border-border text-subtle"}`}
                  data-testid="toggle-wakelock"
                >
                  {wakeLockOn ? "Actif" : "Inactif"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-sm">
          <Link to="/settings" className="text-subtle hover:text-ink" data-testid="link-settings">
            Gérer les notifications
          </Link>
          <span className="mx-2 text-border">·</span>
          <Link to="/my-tickets" className="text-subtle hover:text-ink" data-testid="link-history-2">
            Mon historique
          </Link>
        </div>

        <ConfirmDialog
          open={cancelConfirm}
          title="Annuler votre ticket ?"
          message={
            <>
              <p>Votre place dans la file sera <strong>définitivement perdue</strong>.</p>
              <p className="text-destructive font-bold">Cette action est irréversible.</p>
            </>
          }
          confirmLabel="OK"
          destructive
          onConfirm={performCancel}
          onCancel={() => setCancelConfirm(false)}
          testid="ticket-cancel-confirm"
        />
      </div>
      {/* Discrete delayed prompt — only shown when a ticket is active */}
      <NotifPermissionPrompt ticket={ticket} />
    </>
  );
}
